import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { X, Plus, Trash2, Check, RotateCcw, AlertTriangle } from 'lucide-react';
import { coincide } from '../buscar';
import { parseNum } from '../num';

const fmt2 = (n) => n != null ? '$ ' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmt0 = (n) => n != null ? '$ ' + Math.round(n).toLocaleString('es-AR') : '$ 0';

// El material se compone en su unidad de cálculo (el hierro en kg), pero se
// compra en la de venta (la barra). Sin esto, quien trabaja en barras tiene
// que ir a hacer la cuenta a otro lado para saber con cuánto está trabajando.
const equivalenciaVenta = (m) => {
  if (!m || !m.presentacion || !m.cant_presentacion || m.cant_presentacion <= 0) return null;
  return `${m.cant_presentacion} ${m.unidad || ''} = 1 ${m.presentacion}`;
};

export default function PanelAnalisis({ presupuestoId, linea, onClose, onCostoChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [materiales, setMateriales] = useState([]);
  const [moList, setMoList] = useState([]);
  const [maqList, setMaqList] = useState([]);
  const [addMat, setAddMat] = useState({ material_id: '', cantidad: 1 });
  const [addMO, setAddMO] = useState({ unidad_mo_id: '', horas: 1 });
  const [addMaq, setAddMaq] = useState({ maquinaria_id: '', horas: 1 });
  const [editando, setEditando] = useState(null); // {tipo, id, campo, valor}
  const [modalImportar, setModalImportar] = useState(false);
  const [itemsCatalogo, setItemsCatalogo] = useState([]);
  const [busquedaImport, setBusquedaImport] = useState('');
  const [busquedaMat, setBusquedaMat] = useState(''); // búsqueda inline en selector de material
  const [showMatDropdown, setShowMatDropdown] = useState(false);
  const [importando, setImportando] = useState(false);

  const lineaId = linea?.id;
  useEffect(() => {
    if (lineaId) cargar();
  }, [lineaId]);

  // Si alguien deja este panel abierto mientras cambia un precio en el
  // catálogo (en otra pestaña, o en el celular), acá no había nada que lo
  // refrescara solo: cargar() corre una sola vez, al abrir. Con un panel
  // abierto todo el rato es justo el peor caso — el estudio mira el precio
  // acá para confirmar el cambio, y ve el mismo número viejo. Se refresca
  // solo cada 30s, salvo que haya algo a medio escribir.
  const editandoRef = useRef(editando);
  editandoRef.current = editando;
  useEffect(() => {
    if (!lineaId) return;
    const id = setInterval(() => {
      if (!editandoRef.current) cargar(true);
    }, 30000);
    return () => clearInterval(id);
  }, [lineaId]); // eslint-disable-line

  useEffect(() => {
    Promise.all([
      api.get('/maestros/materiales'),
      api.get('/analisis/mo'),
      api.get('/analisis/maquinaria'),
    ]).then(([m, mo, maq]) => {
      setMateriales(m.data);
      setMoList(mo.data);
      setMaqList(maq.data);
    });
    // Cargar items del catálogo para importar análisis
    api.get('/analisis/items').then(r => setItemsCatalogo(r.data || [])).catch(() => {});
  }, []);

  const cargar = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis`);
      setData(res.data);
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  };

  const recargar = async () => {
    const res = await api.get(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis`);
    setData(res.data);
    // Forzar recálculo de snaps y esperar que termine antes de recargar presupuesto
    try {
      await api.post(`/presupuestos/${presupuestoId}/recalcular-overrides`);
    } catch(e) { console.error('recalcular-overrides:', e); }
    // Pequeña pausa para que el backend commitee los cambios
    await new Promise(r => setTimeout(r, 300));
    if (onCostoChange) onCostoChange(linea.id, res.data.totales.total);
  };

  const handleResetear = async () => {
    if (!window.confirm('¿Restablecer el análisis original? Se perderán todos los cambios de este presupuesto.')) return;
    await api.delete(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis`);
    recargar();
  };

  const handleImportarDesdeCatalogo = async (itemObraId) => {
    setImportando(true);
    try {
      // Obtener el análisis del item del catálogo
      const res = await api.get(`/analisis/items/${itemObraId}`);
      const analisis = res.data;
      // Borrar override actual si existe
      await api.delete(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis`).catch(() => {});
      // Agregar materiales
      for (const m of (analisis.lineas_material || [])) {
        await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/materiales`,
          { material_id: m.material_id, cantidad: parseNum(m.cantidad) });
      }
      // Agregar MO
      for (const mo of (analisis.lineas_mo || [])) {
        await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/mo`,
          { unidad_mo_id: mo.unidad_mo_id, horas: parseNum(mo.horas) });
      }
      // Agregar Maquinaria
      for (const maq of (analisis.lineas_maquinaria || [])) {
        await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/maquinaria`,
          { maquinaria_id: maq.maquinaria_id, horas: parseNum(maq.horas) });
      }
      setModalImportar(false);
      setBusquedaImport('');
      await recargar();
    } catch(e) {
      alert('Error al importar: ' + (e.message || 'desconocido'));
    }
    setImportando(false);
  };

  // En obra nunca rinde el 100%: se corta, se rompe, sobra recorte. Va por
  // ítem y no por material — "esta tabiquería tiene 8% de desperdicio" — y se
  // aplica a todos los materiales del análisis de una sola vez. El ajuste fino
  // por material individual sigue disponible desde el listado de materiales.
  const guardarDesperdicioItem = async (pct) => {
    const n = parseFloat(String(pct).replace(',', '.'));
    if (isNaN(n) || n < 0 || n > 100) return;
    try {
      await api.patch(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/desperdicio`,
        { desperdicio_pct: n });
      await cargar();
      onCostoChange && onCostoChange();
    } catch (e) { /* el panel ya muestra el valor viejo */ }
  };

  // Refuerzo de pared: un hierro del 8 cada 40cm, horizontal. Solo aplica a
  // ítems de mampostería (pared/muro); en cualquier otro ítem no se muestra.
  const handleToggleRefuerzo = async (con_refuerzo) => {
    try {
      await api.patch(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/refuerzo-mamposteria`,
        { con_refuerzo });
      await cargar();
      onCostoChange && onCostoChange();
    } catch (e) { alert('No se pudo cambiar el refuerzo: ' + (e.response?.data?.detail || e.message)); }
  };

  // Materiales
  const handleAgregarMat = async () => {
    if (!addMat.material_id) return;
    await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/materiales`,
      { material_id: parseInt(addMat.material_id), cantidad: parseNum(addMat.cantidad) });
    setAddMat({ material_id: '', cantidad: 1 });
    recargar();
  };
  const handleEditarMat = async (id, campo, valor) => {
    const body = {};
    if (campo === 'cantidad') body.cantidad = parseNum(valor);
    if (campo === 'precio_manual') body.precio_manual = parseNum(valor) || null;
    await api.patch(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/materiales/${id}`, body);
    setEditando(null); recargar();
  };
  const handleEliminarMat = async (id) => {
    await api.delete(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/materiales/${id}`);
    recargar();
  };

  // MO
  const handleAgregarMO = async () => {
    if (!addMO.unidad_mo_id) return;
    await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/mo`,
      { unidad_mo_id: parseInt(addMO.unidad_mo_id), horas: parseNum(addMO.horas) });
    setAddMO({ unidad_mo_id: '', horas: 1 });
    recargar();
  };
  const handleEditarMO = async (id, campo, valor) => {
    const body = {};
    if (campo === 'horas') body.horas = parseNum(valor);
    if (campo === 'costo_manual') body.costo_manual = parseNum(valor) || null;
    await api.patch(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/mo/${id}`, body);
    setEditando(null); recargar();
  };
  const handleEliminarMO = async (id) => {
    await api.delete(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/mo/${id}`);
    recargar();
  };

  // Maquinaria
  const handleAgregarMaq = async () => {
    if (!addMaq.maquinaria_id) return;
    await api.post(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/maquinaria`,
      { maquinaria_id: parseInt(addMaq.maquinaria_id), horas: parseNum(addMaq.horas) });
    setAddMaq({ maquinaria_id: '', horas: 1 });
    recargar();
  };
  const handleEditarMaq = async (id, campo, valor) => {
    const body = {};
    if (campo === 'horas') body.horas = parseNum(valor);
    if (campo === 'costo_manual') body.costo_manual = parseNum(valor) || null;
    await api.patch(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/maquinaria/${id}`, body);
    setEditando(null); recargar();
  };
  const handleEliminarMaq = async (id) => {
    await api.delete(`/presupuestos/${presupuestoId}/lineas/${linea.id}/analisis/maquinaria/${id}`);
    recargar();
  };

  if (!linea) return null;

  return (
    <div className="panel-lateral" style={{
      width: 420, borderLeft: '1px solid var(--border)', background: 'var(--surface)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}>
            {linea.nombre_override || linea.nombre_item || linea.nombre_libre}
          </div>
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--muted)', marginTop: 3 }}>
            {linea.tipo === 'libre' ? 'Análisis desglosado · ítem libre' : 'Análisis de costos'}{data?.tiene_override && <span style={{ color: 'var(--warn)' }}> · modificado</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setModalImportar(true)} title="Importar análisis desde el catálogo" style={{ fontSize: 10 }}>
            Importar
          </button>
          {data?.tiene_override && (
            <button className="btn btn-secondary btn-sm" onClick={handleResetear} title="Restablecer análisis original">
              <RotateCcw size={12} />
            </button>
          )}
          <button data-tour="cerrar-analisis" className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
      </div>

      {/* Aviso override */}
      {data?.tiene_override && (
        <div style={{ padding: '6px 14px', background: 'rgba(251,191,36,0.08)', borderBottom: '1px solid rgba(251,191,36,0.2)', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--warn)' }}>
          <AlertTriangle size={12} />
          Análisis personalizado para este presupuesto
        </div>
      )}

      {/* Totales */}
      {data && (
        <div data-tour="analisis-totales" style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16 }}>
          {[
            { label: 'Mat', val: data.totales.materiales, color: 'var(--mat)' },
            { label: 'MO', val: data.totales.mano_obra, color: 'var(--mo)' },
            { label: 'Maq', val: data.totales.maquinaria, color: 'var(--maq)' },
            { label: 'Total', val: data.totales.total, color: 'var(--ejec)' },
          ].map(t => (
            <div key={t.label}>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--muted)' }}>{t.label}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, color: t.color }}>{fmt0(t.val)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Refuerzo de mampostería — solo mampostería (ladrillo/HCCA/bloque), no
          cualquier "muro" (steel frame, durlock, etc. no llevan esto). El
          rubro es la señal más confiable; el nombre del ítem es respaldo para
          cuando no viene categorizado. */}
      {data && (/mamposter/i.test(linea?.categoria_nombre || '')
        || /mamposter|ladrillo|hcca|bloque/i.test(data.item_nombre || '')) && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600 }}>Refuerzo</div>
            <div style={{ fontSize: 9.5, color: 'var(--muted)' }}>Hierro del 8 cada 40cm, horizontal</div>
          </div>
          <button onClick={() => handleToggleRefuerzo(!data.refuerzo_mamposteria)}
            title={data.refuerzo_mamposteria ? 'Con refuerzo — click para sacarlo' : 'Sin refuerzo — click para agregarlo'}
            style={{ width: 38, height: 21, borderRadius: 11, border: 'none', cursor: 'pointer', position: 'relative',
                     background: data.refuerzo_mamposteria ? 'var(--accent)' : 'var(--border2)', transition: 'background .15s', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: 2, left: data.refuerzo_mamposteria ? 19 : 2, width: 17, height: 17,
                           borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
          </button>
        </div>
      )}

      {/* Contenido */}
      <div data-tour="analisis-secciones" style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {loading ? <div className="loading">Cargando...</div> : data && (
          <>
            {/* MATERIALES */}
            <Seccion titulo="1/3) Materiales" color="var(--mat)"
              extra={<DesperdicioItem lineasMat={data.lineas_mat} onGuardar={guardarDesperdicioItem} />}>
              <TablaLineas
                lineas={data.lineas_mat.map(l => ({
                  id: l.id,
                  nombre: l.nombre, sub: l.codigo,
                  val1: l.cantidad, label1: 'Cant', unidad: l.unidad,
                  val2: l.precio_unitario, label2: 'P.Unit',
                  val2_manual: l.precio_manual,
                  desperdicio: l.desperdicio_pct || 0,
                  equivalencia: equivalenciaVenta(l),
                  subtotal: l.subtotal,
                }))}
                editando={editando} setEditando={setEditando}
                onEditar={(id, campo, val) => handleEditarMat(id, campo, val)}
                onEliminar={handleEliminarMat}
                campo1="cantidad" campo2="precio_manual"
              />
              <FilaAgregar>
                <div style={{ flex: 2, position: 'relative' }}>
                  <input
                    className="input" style={{ fontSize: 11, width: '100%', boxSizing: 'border-box' }}
                    placeholder="Buscar material..."
                    value={busquedaMat}
                    onChange={e => { setBusquedaMat(e.target.value); setShowMatDropdown(true); setAddMat(p => ({ ...p, material_id: '' })); }}
                    onFocus={() => setShowMatDropdown(true)}
                    onBlur={() => setTimeout(() => setShowMatDropdown(false), 150)}
                  />
                  {showMatDropdown && busquedaMat.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                      {materiales
                        .filter(m => coincide(m.nombre, busquedaMat) || coincide(m.codigo, busquedaMat))
                        .slice(0, 30)
                        .map(m => (
                          <div key={m.id}
                            onMouseDown={() => { setAddMat(p => ({ ...p, material_id: m.id })); setBusquedaMat(m.codigo + ' — ' + m.nombre); setShowMatDropdown(false); }}
                            style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid var(--border)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <span style={{ fontFamily: 'var(--mono)', color: 'var(--muted)', marginRight: 6 }}>{m.codigo}</span>
                            {m.nombre}
                            <span style={{ float: 'right', color: 'var(--muted)', fontSize: 10 }}>{m.unidad}</span>
                            {equivalenciaVenta(m) && (
                              <div style={{ fontSize: 9.5, color: 'var(--accent2)', marginTop: 1 }}>{equivalenciaVenta(m)}</div>
                            )}
                          </div>
                        ))}
                      {materiales.filter(m => coincide(m.nombre, busquedaMat) || coincide(m.codigo, busquedaMat)).length === 0 && (
                        <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--muted)' }}>Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
                <input type="text" inputMode="decimal" className="input input-mono" style={{ width: 70, fontSize: 11 }}
                  placeholder="Cant" value={addMat.cantidad} onChange={e => setAddMat(p => ({ ...p, cantidad: e.target.value }))} />
                <button className="btn btn-primary btn-sm" onClick={() => { handleAgregarMat(); setBusquedaMat(''); }}><Plus size={11} /></button>
              </FilaAgregar>
            </Seccion>

            {/* MANO DE OBRA */}
            <Seccion titulo="2/3) Mano de obra" color="var(--mo)">
              <TablaLineas
                lineas={data.lineas_mo.map(l => ({
                  id: l.id, nombre: l.funcion,
                  val1: l.horas, label1: 'Hs',
                  val2: l.costo_hora, label2: '$/hs',
                  val2_manual: l.costo_manual,
                  subtotal: l.subtotal,
                }))}
                editando={editando} setEditando={setEditando}
                onEditar={(id, campo, val) => handleEditarMO(id, campo, val)}
                onEliminar={handleEliminarMO}
                campo1="horas" campo2="costo_manual"
              />
              <FilaAgregar>
                <select className="input" style={{ flex: 2, fontSize: 11 }}
                  value={addMO.unidad_mo_id} onChange={e => setAddMO(p => ({ ...p, unidad_mo_id: e.target.value }))}>
                  <option value="">Función...</option>
                  {moList.map(m => <option key={m.id} value={m.id}>{m.funcion || m.nombre}</option>)}
                </select>
                <input type="text" inputMode="decimal" className="input input-mono" style={{ width: 70, fontSize: 11 }}
                  placeholder="Hs" value={addMO.horas} onChange={e => setAddMO(p => ({ ...p, horas: e.target.value }))} />
                <button className="btn btn-primary btn-sm" onClick={handleAgregarMO}><Plus size={11} /></button>
              </FilaAgregar>
            </Seccion>

            {/* MAQUINARIA */}
            <Seccion titulo="3/3) Maq / Eq / Herr" color="var(--maq)">
              <TablaLineas
                lineas={data.lineas_maq.map(l => ({
                  id: l.id, nombre: l.nombre,
                  val1: l.horas, label1: 'Hs',
                  val2: l.costo_hora, label2: '$/hs',
                  val2_manual: l.costo_manual,
                  subtotal: l.subtotal,
                }))}
                editando={editando} setEditando={setEditando}
                onEditar={(id, campo, val) => handleEditarMaq(id, campo, val)}
                onEliminar={handleEliminarMaq}
                campo1="horas" campo2="costo_manual"
              />
              <FilaAgregar>
                <select className="input" style={{ flex: 2, fontSize: 11 }}
                  value={addMaq.maquinaria_id} onChange={e => setAddMaq(p => ({ ...p, maquinaria_id: e.target.value }))}>
                  <option value="">Equipo...</option>
                  {maqList.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
                <input type="text" inputMode="decimal" className="input input-mono" style={{ width: 70, fontSize: 11 }}
                  placeholder="Hs" value={addMaq.horas} onChange={e => setAddMaq(p => ({ ...p, horas: e.target.value }))} />
                <button className="btn btn-primary btn-sm" onClick={handleAgregarMaq}><Plus size={11} /></button>
              </FilaAgregar>
            </Seccion>
          </>
        )}
      </div>

      {/* MODAL IMPORTAR DESDE CATÁLOGO */}
      {modalImportar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModalImportar(false)}>
          <div style={{ background: 'var(--surface)', borderRadius: 10, padding: 20, width: 'min(480px, 95vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Importar análisis del catálogo</div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)' }} onClick={() => setModalImportar(false)}><X size={16} /></button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Elegí un ítem del catálogo para copiar su análisis de materiales, MO y maquinaria a este ítem.
            </div>
            <input className="input" placeholder="Buscar por código o nombre..."
              value={busquedaImport} onChange={e => setBusquedaImport(e.target.value)}
              autoFocus style={{ fontSize: 12 }} />
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: 340, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {itemsCatalogo
                .filter(it => !busquedaImport || coincide(it.nombre, busquedaImport) || coincide(it.codigo, busquedaImport))
                .slice(0, 50)
                .map(it => (
                  <button key={it.id}
                    disabled={importando}
                    onClick={() => handleImportarDesdeCatalogo(it.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer', textAlign: 'left', opacity: importando ? 0.5 : 1 }}>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', minWidth: 60 }}>{it.codigo}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{it.nombre}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                        MAT {fmt0(it.costo_materiales)} · MO {fmt0(it.costo_mano_obra)} · MAQ {fmt0(it.costo_maquinaria)}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--accent2)', fontWeight: 600 }}>{importando ? '...' : '→'}</div>
                  </button>
                ))}
              {itemsCatalogo.filter(it => !busquedaImport || coincide(it.nombre, busquedaImport) || coincide(it.codigo, busquedaImport)).length === 0 && (
                <div style={{ color: 'var(--muted)', fontSize: 12, padding: 12, textAlign: 'center' }}>Sin resultados</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Seccion({ titulo, color, extra, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{titulo}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

// Un solo % de desperdicio para todos los materiales del ítem, no por línea.
function DesperdicioItem({ lineasMat, onGuardar }) {
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState('');
  const valores = [...new Set(lineasMat.map(l => l.desperdicio_pct || 0))];
  const uniforme = valores.length <= 1;
  const actual = uniforme ? (valores[0] || 0) : null;

  if (lineasMat.length === 0) return null;

  if (editando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, textTransform: 'none', fontWeight: 400 }} onClick={e => e.stopPropagation()}>
        <input type="text" inputMode="decimal" autoFocus value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { onGuardar(val); setEditando(false); } if (e.key === 'Escape') setEditando(false); }}
          style={{ width: 40, fontSize: 10, textAlign: 'right', padding: '1px 4px', borderRadius: 3, border: '1px solid var(--warn)', background: 'var(--bg)', color: 'var(--text)' }} />
        <span style={{ fontSize: 9 }}>%</span>
        <button onClick={() => { onGuardar(val); setEditando(false); }} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', padding: 0 }}><Check size={10} /></button>
      </div>
    );
  }
  return (
    <span onClick={() => { setEditando(true); setVal(String(actual ?? 0)); }}
      title="Desperdicio del ítem: se aplica a todos sus materiales. El ajuste fino por material va en el listado de materiales."
      style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, cursor: 'pointer', color: actual > 0 ? 'var(--warn)' : 'var(--border2)' }}>
      {uniforme ? (actual > 0 ? `Desperdicio +${actual}%` : '+ desperdicio del ítem') : 'Desperdicio: mixto — click para unificar'}
    </span>
  );
}

function TablaLineas({ lineas, editando, setEditando, onEditar, onEliminar, campo1, campo2 }) {
  const [editVal, setEditVal] = useState('');

  if (lineas.length === 0)
    return <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 0 8px' }}>Sin líneas</div>;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
      <tbody>
        {lineas.map(l => {
          const isEdit1 = editando?.id === l.id && editando?.campo === campo1;
          const isEdit2 = editando?.id === l.id && editando?.campo === campo2;
          return (
            <tr key={l.id} style={{ borderBottom: '1px solid rgba(46,46,56,0.4)' }}>
              <td style={{ padding: '5px 0', fontSize: 11, lineHeight: 1.3 }}>
                <div>{l.nombre}</div>
                {l.sub && <div style={{ fontSize: 9, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{l.sub}</div>}
                {l.desperdicio > 0 && (
                  <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>
                    Compra: {(Number(l.val1) * (1 + Number(l.desperdicio) / 100)).toLocaleString('es-AR', { maximumFractionDigits: 3 })}{l.unidad ? ' ' + l.unidad : ''}
                  </div>
                )}
                {l.equivalencia && (
                  <div style={{ fontSize: 9, color: 'var(--accent2)', marginTop: 1 }}>{l.equivalencia}</div>
                )}
              </td>
              {/* Campo 1 (cantidad/horas) */}
              <td style={{ padding: '5px 4px', textAlign: 'right', width: 70 }}>
                {isEdit1 ? (
                  <div style={{ display: 'flex', gap: 2 }}>
                    <input type="text" inputMode="decimal" autoFocus value={editVal}
                      onChange={e => setEditVal(e.target.value)}
                      style={{ width: 55, background: 'var(--bg)', border: '1px solid var(--accent2)', borderRadius: 3, padding: '2px 4px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 11, textAlign: 'right' }} />
                    <button style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer' }} onClick={() => onEditar(l.id, campo1, editVal)}><Check size={11} /></button>
                    <button style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer' }} onClick={() => setEditando(null)}><X size={10} /></button>
                  </div>
                ) : (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', padding: '2px 4px', borderRadius: 3 }}
                    onClick={() => { setEditando({ id: l.id, campo: campo1 }); setEditVal(l.val1); }}
                    title={`Editar ${l.label1}`}>
                    {l.val1}
                    {l.unidad && <span style={{ fontSize: 9, color: 'var(--muted)', marginLeft: 3 }}>{l.unidad}</span>}
                  </span>
                )}
              </td>
              {/* Campo 2 (precio/costo) */}
              <td style={{ padding: '5px 4px', textAlign: 'right', width: 90 }}>
                {isEdit2 ? (
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <input type="text" inputMode="decimal" autoFocus value={editVal}
                        onChange={e => setEditVal(e.target.value)}
                        style={{ width: 70, background: 'var(--bg)', border: '1px solid var(--warn)', borderRadius: 3, padding: '2px 4px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 11, textAlign: 'right' }} />
                      <button style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer' }} onClick={() => onEditar(l.id, campo2, editVal)}><Check size={11} /></button>
                      <button style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer' }} onClick={() => setEditando(null)}><X size={10} /></button>
                    </div>
                    {/* Recién ahora, mientras lo está tipeando, es el único
                        momento en que de verdad puede leer esto — después el
                        precio queda naranja para siempre y nadie vuelve a ver
                        una explicación de por qué. */}
                    {l.val2_manual == null && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 3, background: 'var(--warn)', color: '#fff',
                                    fontSize: 9, lineHeight: 1.3, padding: '4px 7px', borderRadius: 4, width: 150, zIndex: 5,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
                        Va a dejar de seguir el precio del catálogo para este ítem
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span
                      style={{ fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', padding: '2px 4px', borderRadius: 3, color: l.val2_manual ? 'var(--warn)' : 'var(--muted)' }}
                      onClick={() => { setEditando({ id: l.id, campo: campo2 }); setEditVal(l.val2_manual || l.val2); }}
                      title={l.val2_manual ? 'Precio manual — click para editar' : 'Precio de maestro — click para personalizar'}>
                      {fmt2(l.val2)}
                      {l.val2_manual && <span style={{ fontSize: 9, marginLeft: 2 }}>✎</span>}
                    </span>
                    {/* Un precio puesto a mano deja de seguir al catálogo para
                        siempre — bajarlo o subirlo allá no cambia nada acá. Sin
                        este botón, la única forma de volver a engancharlo era
                        entrar a editar y borrar el campo, algo que nadie
                        adivina solo. */}
                    {l.val2_manual != null && (
                      <button
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, display: 'flex' }}
                        onClick={(e) => { e.stopPropagation(); onEditar(l.id, campo2, ''); }}
                        title="Quitar precio manual — volver a seguir el precio del catálogo">
                        <RotateCcw size={10} />
                      </button>
                    )}
                  </span>
                )}
              </td>
              <td style={{ padding: '5px 4px', textAlign: 'right', width: 80, fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--ejec)' }}>
                {fmt2(l.subtotal)}
              </td>
              <td style={{ padding: '5px 0', width: 20 }}>
                <button style={{ background: 'none', border: 'none', color: 'var(--border2)', cursor: 'pointer', fontSize: 14 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--border2)'}
                  onClick={() => onEliminar(l.id)}>×</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FilaAgregar({ children }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
      {children}
    </div>
  );
}
